import type { HateoasLink } from "../../utils/hateoas-link";
import type { PartyAuthenticationSessionStatusUpdate } from "./party-authentication-service";
import type {
  ExternalIdentifier,
  IdentityIdentifier,
  PartyAuthenticationSession,
} from "./party-authentication-session";
import { PartyAuthenticationSessionStatus } from "./party-authentication-session";

export class PartyAuthenticationSessionState {
  constructor(private readonly session: PartyAuthenticationSession) {}

  public update(statusUpdate: PartyAuthenticationSessionStatusUpdate): this {
    if (this.session) {
      this.session.status = statusUpdate.status;
      this.session.links = statusUpdate.links;
    }

    return this;
  }

  public get token(): string {
    return this.session.token;
  }

  public get identityIdentifier(): IdentityIdentifier | null | undefined {
    return this.session.identityIdentifier;
  }

  public get externalIdentifier(): ExternalIdentifier | null | undefined {
    return this.session.externalIdentifier;
  }

  public get description(): string | null | undefined {
    return this.session.data?.description;
  }

  public get initiatedAt(): Date {
    return this.session.initiatedAt;
  }

  public get expiredAt(): Date {
    return this.session.expiredAt;
  }

  public get currentStatus(): PartyAuthenticationSessionStatus {
    return this.isActive() && this.session.expiredAt <= new Date()
      ? PartyAuthenticationSessionStatus.EXPIRED
      : this.session.status;
  }

  public canStart(): boolean {
    return this.isActive() && !this.isExpired();
  }

  public isActive(): boolean {
    return [PartyAuthenticationSessionStatus.PENDING, PartyAuthenticationSessionStatus.STARTED].includes(
      this.session.status,
    );
  }

  public isExpired(): boolean {
    return this.currentStatus === PartyAuthenticationSessionStatus.EXPIRED;
  }

  public isValid(): boolean {
    return [
      PartyAuthenticationSessionStatus.PENDING,
      PartyAuthenticationSessionStatus.STARTED,
      PartyAuthenticationSessionStatus.DONE,
    ].includes(this.currentStatus);
  }

  public isError(): boolean {
    return [
      PartyAuthenticationSessionStatus.CANCELLED,
      PartyAuthenticationSessionStatus.REFUSED,
      PartyAuthenticationSessionStatus.FAILED,
    ].includes(this.currentStatus);
  }

  public isDone() {
    return this.session.status === PartyAuthenticationSessionStatus.DONE;
  }

  public getStatus(): PartyAuthenticationSessionStatus {
    return this.session.status;
  }

  public canCancel(): boolean {
    return !!this.session.links.authenticationSessionCancel;
  }

  public getCancelLink(): HateoasLink | null {
    return this.session.links.authenticationSessionCancel ?? null;
  }

  public canRefuse(): boolean {
    return (
      this.session.status === PartyAuthenticationSessionStatus.PENDING ||
      this.session.status === PartyAuthenticationSessionStatus.STARTED
    );
  }

  public canSendSmsOtp(): boolean {
    return !!this.session.links.authenticationSessionSendSMSOTP;
  }

  public getSmsOtpLink(): HateoasLink | null {
    return this.session.links.authenticationSessionSendSMSOTP ?? null;
  }

  public canVerifySmsOtp(): boolean {
    return !!this.session.links.authenticationSessionVerifySMSOTP;
  }

  public getVerifySmsOtpLink(): HateoasLink | null {
    return this.session.links.authenticationSessionVerifySMSOTP ?? null;
  }

  public canVerifyPincode(): boolean {
    return !!this.session.links.authenticationSessionVerifyPincode;
  }

  public getVerifyPincodeLink(): HateoasLink | null {
    return this.session.links.authenticationSessionVerifyPincode ?? null;
  }

  public canVerifyTrustedDevice(): boolean {
    return !!this.session.links.authenticationSessionVerifyTrustedDevice;
  }

  public getVerifyTrustedDeviceLink(): HateoasLink | null {
    // ATTENTION: remove this replace code when the trusted device link is fixed
    const hateoasLink = this.session.links.authenticationSessionVerifyTrustedDevice ?? null;
    if (hateoasLink) {
      const customHateoasLink = {
        ...hateoasLink,
        href: hateoasLink.href.replace("/methods/devices/verify", "/methods/device/verify"),
      };
      return customHateoasLink;
    }
    return hateoasLink;
  }

  public canOnlyVerifyPincode(): boolean {
    return this.canVerifyPincode() && !this.canVerifySmsOtp();
  }

  public canOnlyVerifySmsOtp(): boolean {
    return !this.canVerifyPincode() && this.canVerifySmsOtp();
  }

  public canVerifyPincodeAndSmsOtp(): boolean {
    return this.canVerifyPincode() && this.canVerifySmsOtp();
  }

  public canVerifyTrustedDeviceAndPincode(secret: string | null): boolean {
    const allMethodsAreAvailable = this.canVerifyTrustedDevice() && this.canVerifyPincode() && this.canVerifySmsOtp();
    return !allMethodsAreAvailable && !!secret && this.canVerifyTrustedDevice() && this.canVerifyPincode();
  }
}
